import { useCallback, useState } from 'react';
import { getFonts } from '../../../../../api/fonts';
import { getRestaurantFontSettings, saveRestaurantFontSettings } from '../../../../../api/restaurantTypography';
import { FontListItemInterface } from '../../../../../types/FontInterface';
import { FontCategoryId, FontRow } from '../../designPageTypes';
import {
  createDefaultFontRows,
  fromApiFontSettings,
  isValidFontPayload,
  normalizeHex,
  toApiFontSettings
} from '../../designPageUtils';
import useDesignTabSettings, { DesignTabLoadActions } from '../../hooks/useDesignTabSettings';

type FontState = Record<FontCategoryId, FontRow>;
type FontPayload = ReturnType<typeof toApiFontSettings>;

const useDesignFontSettings = (isActive: boolean) => {
  const [fontChoices, setFontChoices] = useState<FontListItemInterface[]>([]);

  const loadFontSettings = useCallback(
    async (
      _restaurantId: number,
      { setState, setSettingsError, markPersisted, setSettingsReady }: DesignTabLoadActions<FontState, FontPayload>
    ) => {
      const [fontsCatalogResult, fontSettingsResult] = await Promise.allSettled([
        getFonts(),
        getRestaurantFontSettings()
      ]);

      const selectableFonts = fontsCatalogResult.status === 'fulfilled' ? fontsCatalogResult.value.fonts : [];
      setFontChoices(selectableFonts);

      if (fontSettingsResult.status === 'fulfilled') {
        const loadedFontRows = fromApiFontSettings(fontSettingsResult.value.fonts, selectableFonts);
        setState(loadedFontRows);
        markPersisted(toApiFontSettings(loadedFontRows));
        setSettingsReady(true);
      } else {
        const defaultFontRows = createDefaultFontRows(selectableFonts[0]?.title ?? '');
        setState(defaultFontRows);
        markPersisted(toApiFontSettings(defaultFontRows));
        setSettingsError('Unable to load font settings. You can still edit and save below.');
        setSettingsReady(true);
      }
    },
    []
  );

  const {
    settingsLoading,
    settingsError,
    state: fonts,
    setState: setFonts
  } = useDesignTabSettings<FontState, FontPayload>({
    isActive,
    initialState: () => createDefaultFontRows(''),
    toPayload: toApiFontSettings,
    save: {
      toastId: 'design-font-settings',
      savingMessage: 'Saving design settings…',
      savedMessage: 'Design settings saved',
      errorMessage: 'Could not save design settings',
      isValid: isValidFontPayload,
      save: saveRestaurantFontSettings
    },
    load: loadFontSettings
  });

  const setFamily = useCallback(
    (id: FontCategoryId, family: string) => {
      setFonts((prev) => ({ ...prev, [id]: { ...prev[id], family } }));
    },
    [setFonts]
  );

  const setFontColor = useCallback(
    (id: FontCategoryId, color: string) => {
      const normalized = normalizeHex(color);
      setFonts((prev) => ({
        ...prev,
        [id]: { ...prev[id], color: normalized ?? color }
      }));
    },
    [setFonts]
  );

  return {
    settingsLoading,
    fontSettingsError: settingsError,
    fontChoices,
    fonts,
    setFamily,
    setFontColor
  };
};

export default useDesignFontSettings;
